import React, { useMemo, useCallback, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { IAvailableCurrency } from 'services/BackendApi/types/AvailableCurrenciesResponse';
import { Multiselect, IMultiselectValueLabelPair } from 'ui/Multiselect';
import { getCountryCodeForCurrency } from 'utils/currencyConversion';
import Flag from 'react-world-flags';
import { useAvailableCurrencies } from 'hooks/useAvailableCurrencies';

const CurrencyFlag = ({ currencyCode, className }: { currencyCode: string; className?: string }) => {
  const countryCode = getCountryCodeForCurrency(currencyCode);
  if (!countryCode) {
    return <span className={`inline-block w-[18px] text-center ${className || ''}`}>•</span>;
  }
  return <Flag code={countryCode} className={`w-[18px] h-[13px] inline-block ${className || ''}`} />;
};

interface ICurrencySelectorProps {
  onCurrencyDataChange: (currencyData: IAvailableCurrency | null) => void;
}

export const CurrencySelector = ({ onCurrencyDataChange }: ICurrencySelectorProps) => {
  const history = useHistory();
  const location = useLocation();
  const { sortedCurrencies } = useAvailableCurrencies();
  const hasInitializedFromUrl = useRef(false);

  const options: IMultiselectValueLabelPair[] = useMemo(() => {
    return sortedCurrencies.map(c => ({
      value: c.to.code,
      label: `${c.to.code} - ${c.to.name}`,
      iconHtml: <CurrencyFlag currencyCode={c.to.code} className="mr-[8px]" />,
      iconAlign: 'left' as const,
    }));
  }, [sortedCurrencies]);

  // Read currency from URL
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const selectedCurrency = searchParams.get('currency') || null;

  // Initialize from URL once currencies are loaded
  useEffect(() => {
    if (hasInitializedFromUrl.current || !sortedCurrencies.length) return;
    hasInitializedFromUrl.current = true;

    if (selectedCurrency) {
      const currencyData = sortedCurrencies.find(c => c.to.code === selectedCurrency) || null;
      onCurrencyDataChange(currencyData);
    }
  }, [sortedCurrencies, selectedCurrency, onCurrencyDataChange]);

  const updateUrlCurrency = useCallback(
    (code: string | null) => {
      const newParams = new URLSearchParams(location.search);
      if (code) {
        newParams.set('currency', code);
      } else {
        newParams.delete('currency');
      }
      const newSearch = newParams.toString();
      const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
      history.replace(newUrl);
    },
    [history, location.pathname, location.search]
  );

  const handleUpdate = useCallback(
    (selectedValues: string[]) => {
      const code = selectedValues[0] || null;
      if (!code) {
        onCurrencyDataChange(null);
        updateUrlCurrency(null);
        return;
      }
      const currencyData = sortedCurrencies.find(c => c.to.code === code) || null;
      onCurrencyDataChange(currencyData);
      updateUrlCurrency(code);
    },
    [sortedCurrencies, onCurrencyDataChange, updateUrlCurrency]
  );

  // When closed with a selection, show just the code (e.g. "CAD") instead of "CAD - Canadian Dollar"
  const selectedValuesRenderer = useCallback((label: string) => {
    return label.split(' - ')[0];
  }, []);

  if (!sortedCurrencies.length) {
    return null;
  }

  return (
    <Multiselect
      className="bg-ivory w-[150px]"
      itemsClassname="bg-ivory !w-[250px] right-0 left-auto"
      itemCtaClassName="hover:bg-gray-10"
      options={options}
      selectedValues={selectedCurrency ? [selectedCurrency] : []}
      onUpdate={handleUpdate}
      isSingleSelectMode
      hideCheckboxes
      isCloseOnSelect
      isIncludeClearButton
      placeholderText="Currency"
      selectedValuesRenderer={selectedValuesRenderer}
      showSelectedIcon
    />
  );
};
